import { Slot, Slottable } from "@radix-ui/react-slot";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  type ButtonHTMLAttributes,
  type ComponentType,
  createContext,
  type HTMLAttributes,
  type RefObject,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

export interface NavProps extends HTMLAttributes<HTMLElement> {
  /** Accessible name for the landmark, e.g. "Accounting sections". */
  label: string;
  children?: ReactNode;
}

/** Navigation landmark holding the groups. */
export function Nav({ label, className, children, ...props }: NavProps) {
  return (
    <nav
      aria-label={label}
      className={cn("app-navigation", className)}
      {...props}
    >
      {children}
    </nav>
  );
}

export interface NavGroupProps extends HTMLAttributes<HTMLElement> {
  /** Section heading. Hidden in the collapsed rail, so it is never the only
   * way to tell two items apart. */
  heading?: string;
  children: ReactNode;
}

export function NavGroup({
  heading,
  className,
  children,
  ...props
}: NavGroupProps) {
  return (
    <section
      aria-label={heading}
      className={cn("app-nav-group", className)}
      {...props}
    >
      {heading ? <h2 className="nav-section">{heading}</h2> : null}
      {children}
    </section>
  );
}

type NavIcon = ComponentType<{ className?: string }>;

export interface NavItemProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /**
   * Render as the caller's own element instead of a `<button>` — pass a
   * childless router link and this composes the icon and label into it:
   * `<NavItem asChild icon={Home} label="Overview"><Link to="/" /></NavItem>`.
   * Keeps this package router-agnostic.
   */
  asChild?: boolean;
  icon?: NavIcon;
  label: string;
  /** Marks the current page, and sets `aria-current`. */
  active?: boolean;
  /** End-aligned slot: a count badge, a delivery hint, a chevron. */
  trailing?: ReactNode;
  children?: ReactNode;
}

export function NavItem({
  asChild,
  icon: Icon,
  label,
  active,
  trailing,
  className,
  children,
  ...props
}: NavItemProps) {
  const Component = asChild ? Slot : "button";
  return (
    <Component
      type={asChild ? undefined : "button"}
      aria-current={active ? "page" : undefined}
      // The collapsed rail hides the visible label, so the name must not
      // depend on it.
      aria-label={props["aria-label"] ?? label}
      className={cn(
        "app-nav-item",
        active && "app-nav-item--active",
        className,
      )}
      {...props}
    >
      {Icon ? <Icon className="app-nav-icon" aria-hidden="true" /> : null}
      <span className="app-nav-label">{label}</span>
      {trailing}
      {asChild ? <Slottable>{children}</Slottable> : null}
    </Component>
  );
}

export interface NavExpanderProps {
  icon?: NavIcon;
  label: string;
  /** Controlled — persist this per user so the choice survives a reload. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

/**
 * Nested disclosure inside a group, for entries most people never need.
 * Collapsed by default is the point: it keeps the first read of the nav
 * short without hiding the items behind a separate page.
 */
export function NavExpander({
  icon,
  label,
  open,
  onOpenChange,
  children,
}: NavExpanderProps) {
  return (
    <div className="app-nav-expander">
      <NavItem
        icon={icon}
        label={label}
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        trailing={
          <span aria-hidden="true" className="app-nav-expander__chevron">
            {open ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
          </span>
        }
      />
      {open ? <div className="app-nav-children">{children}</div> : null}
    </div>
  );
}

export interface NavBadgeProps {
  count: number;
  /** Accessible name, e.g. "3 overdue". Falls back to the bare count. */
  label?: string;
}

/** Count badge for a nav item's `trailing` slot. Renders nothing at zero. */
export function NavBadge({ count, label }: NavBadgeProps) {
  if (count <= 0) return null;
  return (
    <output aria-label={label ?? `${count}`} className="app-nav-badge">
      {count > 99 ? "99+" : count}
    </output>
  );
}

/* ------------------------------------------------------------------ sections
 *
 * A section is one icon in the navigation that stands for a set of views.
 * Choosing it opens those views in a flyout flush with the sidebar. The
 * views are the section's children, so a consumer writes them once:
 *
 *   <NavSection id="money-in" icon={Receipt} label="Money in" current={…}>
 *     <NavItem … />
 *   </NavSection>
 */

export interface SidebarNavState {
  collapsed: boolean;
  /** Section whose flyout is open. Nothing is open until a section is chosen. */
  popupId: string | null;
  togglePopup: (id: string) => void;
  closePopup: () => void;
}

const SidebarNavContext = createContext<SidebarNavState | null>(null);
export const SidebarNavProvider = SidebarNavContext.Provider;

/** State behind `<Sidebar>`'s sections. Owned by the sidebar, read by
 * `<NavSection>` through context. */
export function useSidebarNavState(collapsed: boolean): SidebarNavState {
  const [popupId, setPopupId] = useState<string | null>(null);

  // Opening or closing the rail always starts from the section list.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on toggle
  useEffect(() => {
    setPopupId(null);
  }, [collapsed]);

  return useMemo<SidebarNavState>(
    () => ({
      collapsed,
      popupId,
      togglePopup: (id) => setPopupId((open) => (open === id ? null : id)),
      closePopup: () => setPopupId(null),
    }),
    [collapsed, popupId],
  );
}

export interface NavPopupPlacement {
  left: number;
  top: number;
  maxWidth: number;
}

/** Flyout geometry. Flush with the sidebar's right edge, aligned to the
 * chosen section. The top stays a little below the collapse control. When
 * the views do not fit below the section, the flyout moves up instead of
 * growing an internal scroll area. */
export function placeNavPopup(input: {
  sidebar: Pick<DOMRect, "top" | "right" | "bottom">;
  toggle: Pick<DOMRect, "bottom"> | null;
  anchorTop: number;
  popupHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  /** Space kept between the collapse control and the flyout. */
  belowToggle?: number;
  /** Sidebar corner radius. The flyout ends where that curve begins. */
  cornerRadius?: number;
}): NavPopupPlacement {
  const left = input.sidebar.right;
  const belowToggle = input.belowToggle ?? 48;
  const cornerRadius = Math.max(0, input.cornerRadius ?? 0);
  const toggleBottom = input.toggle?.bottom ?? input.sidebar.top;
  const bandTop = Math.max(input.sidebar.top, toggleBottom + belowToggle, 0);
  const bandBottom = Math.min(input.sidebar.bottom - cornerRadius, input.viewportHeight);
  const popupHeight = Math.max(0, input.popupHeight);
  const top = Math.max(
    bandTop,
    Math.min(input.anchorTop, bandBottom - popupHeight),
  );
  return {
    left,
    top,
    maxWidth: Math.max(0, input.viewportWidth - left),
  };
}

export interface NavSectionProps {
  /** Stable id; distinguishes sections from each other. */
  id: string;
  icon?: NavIcon;
  label: string;
  /** This section holds the current page. It stays closed until chosen. */
  current?: boolean;
  /** End-aligned slot before the flyout chevron: a count badge or hint. */
  trailing?: ReactNode;
  /** The section's views, usually `<NavItem>`s. */
  children: ReactNode;
}

export function NavSection(props: NavSectionProps) {
  const state = useContext(SidebarNavContext);
  // Outside a `<Sidebar>` there is nowhere to show a second area, so the views
  // simply render as a labelled group.
  if (!state) {
    return <NavGroup heading={props.label}>{props.children}</NavGroup>;
  }
  return <SidebarSection state={state} {...props} />;
}

function SidebarSection({
  state,
  id,
  icon: Icon,
  label,
  current = false,
  trailing,
  children,
}: NavSectionProps & { state: SidebarNavState }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = state.popupId === id;

  return (
    <div className="app-nav-section">
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="true"
        title={state.collapsed ? label : undefined}
        data-current={current ? "true" : undefined}
        className={cn(
          "app-nav-item",
          "app-nav-section__button",
          current && "app-nav-item--active",
          open && "app-nav-section__button--open",
        )}
        onClick={() => state.togglePopup(id)}
      >
        {Icon ? <Icon className="app-nav-icon" aria-hidden="true" /> : null}
        <span className="app-nav-label">{label}</span>
        {trailing}
        <ChevronRight className="app-nav-section__chevron" aria-hidden="true" />
      </button>
      {open ? (
        <NavPopup anchor={buttonRef} label={label} onClose={state.closePopup}>
          {children}
        </NavPopup>
      ) : null}
    </div>
  );
}

/** Flyout of one section's views, flush with the sidebar. Portalled to
 * `<body>` so the rail's icon styling and overflow clipping do not reach it. */
function NavPopup({
  anchor,
  label,
  onClose,
  children,
}: {
  anchor: RefObject<HTMLButtonElement | null>;
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const popupRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<NavPopupPlacement | null>(null);

  useLayoutEffect(() => {
    const button = anchor.current;
    const popup = popupRef.current;
    const sidebar = button?.closest(".app-sidebar");
    if (!button || !popup || !sidebar) return;
    const toggle = sidebar.querySelector(".app-sidebar-toggle");
    const corner = Number.parseFloat(getComputedStyle(sidebar).borderBottomLeftRadius);
    setPlacement(
      placeNavPopup({
        sidebar: sidebar.getBoundingClientRect(),
        toggle: toggle?.getBoundingClientRect() ?? null,
        anchorTop: button.getBoundingClientRect().top,
        popupHeight: popup.scrollHeight,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        cornerRadius: Number.isFinite(corner) ? corner : 0,
      }),
    );
  }, [anchor]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (popupRef.current?.contains(target) || anchor.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      onClose();
      anchor.current?.focus();
    };
    // The flyout is anchored to a spot in a scrolling list. Scrolling that
    // list, or the window, closes it. Scrolling inside the flyout does not.
    const dismissOnScroll = (event: Event) => {
      const target = event.target as Node | null;
      if (popupRef.current?.contains(target)) return;
      onClose();
    };
    const dismiss = () => onClose();
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", dismiss);
    document.addEventListener("scroll", dismissOnScroll, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", dismiss);
      document.removeEventListener("scroll", dismissOnScroll, true);
    };
  }, [anchor, onClose]);

  return createPortal(
    // biome-ignore lint/a11y/useSemanticElements: labelled region of nav items
    <div
      ref={popupRef}
      role="group"
      aria-label={label}
      className="app-nav-popup"
      style={
        placement
          ? {
              left: placement.left,
              top: placement.top,
              maxWidth: placement.maxWidth,
            }
          : { visibility: "hidden" }
      }
      onClick={(event) => {
        // Choosing a view is the end of the interaction.
        if ((event.target as HTMLElement).closest("a, button")) onClose();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
